/**
 * page-handlers — handlers extraídos de ForecastPage.tsx (P7 split).
 *
 * Cada función es pura: recibe deps + setters como parámetros y retorna
 * el resultado correspondiente. La página las envuelve en useCallback.
 */

import { Dispatch, SetStateAction } from "react";
import { apiClient } from "@/services/apiClient";
import { ProductData } from "./types";

interface ToggleHeaderSelectionOptions {
  filteredAndSortedProducts: ProductData[];
  prev: Set<string>;
}

export function toggleHeaderSelection(
  opts: ToggleHeaderSelectionOptions,
): Set<string> {
  const { filteredAndSortedProducts, prev } = opts;
  const selectable = filteredAndSortedProducts
    .filter((p) => p.activeForPurchase !== false)
    .map((p) => p.unifiedProductId);
  const allSelectableSelected =
    selectable.length > 0 && selectable.every((id) => prev.has(id));
  if (allSelectableSelected) {
    const next = new Set(prev);
    selectable.forEach((id) => next.delete(id));
    return next;
  }
  const next = new Set(prev);
  selectable.forEach((id) => next.add(id));
  return next;
}

interface RunPreselectionOptions {
  months: number;
  filteredAndSortedProducts: ProductData[];
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
}

export async function runPreselection(
  opts: RunPreselectionOptions,
): Promise<Set<string>> {
  const { months, filteredAndSortedProducts, setSelectedIds } = opts;
  try {
    const res = await apiClient.get(
      `/forecasting/product-mapping/recommended-ids?months=${months}`,
    );
    const json = res.data as { status?: string; data?: string[] };
    if (json.status === "success" && Array.isArray(json.data)) {
      const recommended = new Set<string>(json.data);
      const visibleIds = new Set(
        filteredAndSortedProducts
          .filter(
            (p) =>
              recommended.has(p.unifiedProductId) &&
              p.activeForPurchase !== false &&
              Number(p.forecastNextPeriod ?? 0) > 0,
          )
          .map((p) => p.unifiedProductId),
      );
      setSelectedIds(visibleIds);
      return visibleIds;
    }
  } catch (err) {
    console.error("Error fetching recommended ids:", err);
  }
  return new Set();
}

interface MergeSupplierProductsOptions {
  loadedProducts: ProductData[];
  setProducts: Dispatch<SetStateAction<ProductData[]>>;
}

export function mergeSupplierProducts(
  opts: MergeSupplierProductsOptions,
): void {
  const { loadedProducts, setProducts } = opts;
  setProducts((prev) => {
    const map = new Map(
      prev.map((product) => [product.unifiedProductId, product]),
    );
    for (const product of loadedProducts) {
      map.set(product.unifiedProductId, product);
    }
    return Array.from(map.values());
  });
}
